import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Loader2, Mail, Pencil, Phone, Plus, RefreshCw, Search, Stethoscope, Trash2,
  UserPlus, Users, X, User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  PanelTableCard, PanelTableHeader, PanelTableFrame, PanelTableHeadRow, PanelTableHead,
  PanelTableBody, PanelTableRow, PanelTableCell, PanelTableEmpty, PanelTableActions,
} from "@/components/ui/panel-table";
import {
  EntityAvatar, EntityStatPill, EntityInfoBlock, EntityDetailRow, EntityModalHero,
} from "@/components/companies/entity-ui";

const emptyAssistant = () => ({ id: null, ad_soyad: "", telefon: "", email: "" });

const emptyForm = (companyId = "") => ({
  company_id: companyId,
  hospital_id: "",
  ad_soyad: "",
  telefon: "",
  email: "",
  uzmanlik: "",
  notlar: "",
  assistants: [],
});

const DoctorsPage = ({
  apiBase, isDark, textClass, labelClass, cardClass, inputClass,
  companies = [], isSuperAdmin = false, userCompanyId = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(isSuperAdmin ? "all" : String(userCompanyId || ""));
  const [hospitalFilter, setHospitalFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [assistantsOpen, setAssistantsOpen] = useState(false);
  const [assistantDetailOpen, setAssistantDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => emptyForm(isSuperAdmin ? "" : String(userCompanyId || "")));

  const defaultCompanyId = useMemo(() => {
    if (!isSuperAdmin && userCompanyId) return String(userCompanyId);
    if (companyFilter && companyFilter !== "all") return companyFilter;
    return companies[0]?.id ? String(companies[0].id) : "";
  }, [isSuperAdmin, userCompanyId, companyFilter, companies]);

  const stats = useMemo(() => ({
    total: doctors.length,
    assigned: doctors.filter((d) => d.hospital_id).length,
    assistants: doctors.reduce((sum, d) => sum + (d.assistant_count || 0), 0),
  }), [doctors]);

  const fetchHospitals = useCallback(async (companyId) => {
    if (!companyId) { setHospitals([]); return; }
    try {
      const res = await axios.get(`${apiBase}/hospitals`, { params: { company_id: companyId } });
      setHospitals(res.data?.hospitals || []);
    } catch { setHospitals([]); }
  }, [apiBase]);

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isSuperAdmin && companyFilter && companyFilter !== "all") params.company_id = companyFilter;
      if (hospitalFilter === "none") params.hospital_id = "none";
      else if (hospitalFilter && hospitalFilter !== "all") params.hospital_id = hospitalFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const res = await axios.get(`${apiBase}/doctors`, { params });
      setDoctors(res.data?.doctors || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Doktorlar yüklenemedi");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, companyFilter, hospitalFilter, isSuperAdmin, searchTerm]);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  useEffect(() => {
    const cid = companyFilter !== "all" ? companyFilter : (isSuperAdmin ? null : String(userCompanyId || ""));
    if (cid) fetchHospitals(cid);
    else if (isSuperAdmin) setHospitals([]);
  }, [companyFilter, fetchHospitals, isSuperAdmin, userCompanyId]);

  useEffect(() => {
    if (formOpen && form.company_id) fetchHospitals(form.company_id);
  }, [formOpen, form.company_id, fetchHospitals]);

  const modalHospitals = useMemo(() => {
    if (!form.company_id) return [];
    return hospitals.filter((h) => String(h.company_id) === String(form.company_id));
  }, [hospitals, form.company_id]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(defaultCompanyId), assistants: [] });
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      company_id: String(row.company_id || defaultCompanyId),
      hospital_id: row.hospital_id ? String(row.hospital_id) : "",
      ad_soyad: row.ad_soyad || "",
      telefon: row.telefon || "",
      email: row.email || "",
      uzmanlik: row.uzmanlik || "",
      notlar: row.notlar || "",
      assistants: (row.assistants || []).map((a) => ({
        id: a.id || null,
        ad_soyad: a.ad_soyad || "",
        telefon: a.telefon || "",
        email: a.email || "",
      })),
    });
    setDetailOpen(false);
    setAssistantsOpen(false);
    setFormOpen(true);
  };

  const openDetail = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };

  const openAssistantsModal = (row, e) => {
    e?.stopPropagation();
    setSelected(row);
    setAssistantsOpen(true);
  };

  const openAssistantDetail = (assistant, doctor) => {
    setSelected(doctor);
    setSelectedAssistant(assistant);
    setAssistantDetailOpen(true);
  };

  const addAssistant = () => setForm((f) => ({ ...f, assistants: [...(f.assistants || []), emptyAssistant()] }));

  const updateAssistant = (index, key, value) => {
    setForm((f) => {
      const next = [...(f.assistants || [])];
      next[index] = { ...next[index], [key]: value };
      return { ...f, assistants: next };
    });
  };

  const removeAssistant = (index) => {
    setForm((f) => ({ ...f, assistants: (f.assistants || []).filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.ad_soyad.trim()) { toast.error("Doktor adı zorunludur"); return; }
    if (!form.company_id) { toast.error("Firma seçimi zorunludur"); return; }
    setSaving(true);
    try {
      const payload = {
        company_id: Number(form.company_id),
        hospital_id: form.hospital_id ? Number(form.hospital_id) : null,
        ad_soyad: form.ad_soyad.trim(),
        telefon: form.telefon.trim(),
        email: form.email.trim(),
        uzmanlik: form.uzmanlik.trim(),
        notlar: form.notlar.trim(),
        assistants: (form.assistants || []).filter((a) => a.ad_soyad.trim()),
      };
      if (editing?.id) {
        await axios.put(`${apiBase}/doctors?id=${editing.id}`, payload);
        toast.success("Doktor güncellendi");
      } else {
        await axios.post(`${apiBase}/doctors`, payload);
        toast.success("Doktor eklendi");
      }
      setFormOpen(false);
      setDetailOpen(false);
      await fetchDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.ad_soyad}" kaydı silinsin mi?`)) return;
    try {
      await axios.delete(`${apiBase}/doctors?id=${row.id}`);
      toast.success("Doktor silindi");
      setDetailOpen(false);
      setAssistantsOpen(false);
      await fetchDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const rowHover = isDark
    ? "hover:bg-sky-500/5 cursor-pointer group"
    : "hover:bg-sky-50/60 cursor-pointer group";

  return (
    <>
      <PanelTableCard isDark={isDark} className={cardClass}>
        <PanelTableHeader
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          accent="teal"
          icon={Stethoscope}
          title="Doktor Yönetimi"
          description="Doktorlar, asistanlar ve hastane atamaları — satıra tıklayarak detay görün"
          toolbar={(
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={fetchDoctors} disabled={loading} className={isDark ? "border-slate-600" : ""}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button type="button" size="sm" onClick={openCreate} className="bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-600/20">
                <Plus className="w-4 h-4 mr-1" /> Yeni Doktor
              </Button>
            </div>
          )}
        />

        <div className="mx-4 mt-3 flex flex-wrap gap-2">
          <EntityStatPill label="Doktor" value={stats.total} tone="sky" isDark={isDark} />
          <EntityStatPill label="Hastaneli" value={stats.assigned} tone="emerald" isDark={isDark} />
          <EntityStatPill label="Asistan" value={stats.assistants} tone="violet" isDark={isDark} />
        </div>

        <div className={`mx-4 mb-3 mt-3 p-3 rounded-xl border grid grid-cols-1 md:grid-cols-12 gap-3 ${isDark ? "border-slate-700/80 bg-slate-900/40" : "border-sky-100 bg-sky-50/30"}`}>
          {isSuperAdmin && (
            <div className="md:col-span-3">
              <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Firma</label>
              <Select value={companyFilter || "all"} onValueChange={(v) => { setCompanyFilter(v); setHospitalFilter("all"); }}>
                <SelectTrigger className={`h-9 text-xs w-full ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm firmalar</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={isSuperAdmin ? "md:col-span-3" : "md:col-span-4"}>
            <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Hastane</label>
            <Select value={hospitalFilter || "all"} onValueChange={setHospitalFilter}>
              <SelectTrigger className={`h-9 text-xs w-full ${inputClass}`}><SelectValue /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tüm hastaneler</SelectItem>
                <SelectItem value="none">Atanmamış</SelectItem>
                {hospitals.map((h) => <SelectItem key={h.id} value={String(h.id)}>{h.ad}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className={isSuperAdmin ? "md:col-span-6" : "md:col-span-8"}>
            <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Ara</label>
            <div className="relative">
              <Search className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? "text-sky-400" : "text-sky-500"}`} />
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Doktor, telefon, uzmanlık..." className={`h-9 pl-9 ${inputClass}`} />
            </div>
          </div>
        </div>

        <PanelTableFrame isDark={isDark}>
          <PanelTableHeadRow isDark={isDark}>
            <PanelTableHead>Doktor</PanelTableHead>
            {isSuperAdmin && <PanelTableHead>Firma</PanelTableHead>}
            <PanelTableHead>Hastane</PanelTableHead>
            <PanelTableHead>İletişim</PanelTableHead>
            <PanelTableHead className="text-right">Asistan</PanelTableHead>
            <PanelTableHead className="text-right w-24">İşlem</PanelTableHead>
          </PanelTableHeadRow>
          <PanelTableBody>
            {loading ? (
              <PanelTableEmpty colSpan={isSuperAdmin ? 6 : 5} labelClass={labelClass}>
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-sky-500" />
              </PanelTableEmpty>
            ) : doctors.length === 0 ? (
              <PanelTableEmpty colSpan={isSuperAdmin ? 6 : 5} labelClass={labelClass}>
                Doktor kaydı yok
              </PanelTableEmpty>
            ) : doctors.map((row) => (
              <PanelTableRow key={row.id} isDark={isDark} className={rowHover} onClick={() => openDetail(row)}>
                <PanelTableCell>
                  <div className="flex items-center gap-3">
                    <EntityAvatar name={row.ad_soyad} theme="sky" icon={Stethoscope} />
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${textClass} group-hover:text-sky-500 transition-colors`}>{row.ad_soyad}</div>
                      {row.uzmanlik && (
                        <Badge className={`mt-1 text-[10px] ${isDark ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/25" : "bg-cyan-50 text-cyan-700 border-cyan-200"}`}>
                          {row.uzmanlik}
                        </Badge>
                      )}
                    </div>
                  </div>
                </PanelTableCell>
                {isSuperAdmin && (
                  <PanelTableCell>
                    <Badge className={isDark ? "bg-violet-500/15 text-violet-300 border-violet-500/25" : "bg-violet-50 text-violet-700 border-violet-200"}>
                      {row.company_name || row.company_id}
                    </Badge>
                  </PanelTableCell>
                )}
                <PanelTableCell className="text-xs">
                  {row.hospital_ad ? (
                    <span className={`inline-flex items-center gap-1 ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
                      <Building2 className="w-3 h-3" />{row.hospital_ad}
                    </span>
                  ) : (
                    <span className={labelClass}>Atanmamış</span>
                  )}
                </PanelTableCell>
                <PanelTableCell className="text-xs">
                  <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-emerald-500" />{row.telefon || "—"}</div>
                </PanelTableCell>
                <PanelTableCell className="text-right" onClick={(e) => openAssistantsModal(row, e)}>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      (row.assistant_count ?? 0) > 0
                        ? isDark ? "bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 border border-violet-500/30" : "bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200"
                        : isDark ? "bg-slate-800 text-slate-500 border border-slate-700" : "bg-gray-100 text-gray-400 border border-gray-200"
                    }`}
                  >
                    <Users className="w-3 h-3" />{row.assistant_count ?? 0}
                  </button>
                </PanelTableCell>
                <PanelTableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <PanelTableActions>
                    <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(row)} title="Düzenle"><Pencil className="w-4 h-4" /></Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => handleDelete(row)} className={isDark ? "text-rose-400" : "text-rose-600"} title="Sil"><Trash2 className="w-4 h-4" /></Button>
                  </PanelTableActions>
                </PanelTableCell>
              </PanelTableRow>
            ))}
          </PanelTableBody>
        </PanelTableFrame>
      </PanelTableCard>

      {/* Doktor detay modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`max-w-lg p-0 overflow-hidden border-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:hover:opacity-100 [&>button]:hover:bg-white/10 ${isDark ? "bg-slate-900 text-white" : "bg-white"}`}>
          {selected && (
            <>
              <EntityModalHero
                name={selected.ad_soyad}
                subtitle={selected.uzmanlik || "Doktor"}
                theme="sky"
                icon={Stethoscope}
                badges={[
                  selected.hospital_ad || "Hastane atanmamış",
                  `${selected.assistant_count ?? 0} asistan`,
                  selected.company_name,
                ].filter(Boolean)}
              />
              <div className="px-6 pt-4 pb-2 space-y-3">
                <EntityInfoBlock title="İletişim" icon={Phone} tone="emerald" isDark={isDark}>
                  <EntityDetailRow label="Telefon" value={selected.telefon} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                  <EntityDetailRow label="E-posta" value={selected.email} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>
                <EntityInfoBlock title="Hastane Ataması" icon={Building2} tone="indigo" isDark={isDark}>
                  <EntityDetailRow label="Hastane" value={selected.hospital_ad} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Firma" value={selected.company_name} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>
                {selected.notlar && (
                  <EntityInfoBlock title="Notlar" tone="sky" isDark={isDark}>
                    <p className={`text-sm ${textClass}`}>{selected.notlar}</p>
                  </EntityInfoBlock>
                )}
                {(selected.assistants || []).length > 0 && (
                  <EntityInfoBlock title="Asistanlar" icon={Users} tone="violet" isDark={isDark}>
                    <div className="space-y-2 mt-1">
                      {(selected.assistants || []).map((a) => (
                        <button
                          key={a.id || a.ad_soyad}
                          type="button"
                          onClick={() => openAssistantDetail(a, selected)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg border text-left transition-colors ${
                            isDark ? "border-violet-500/20 bg-violet-500/5 hover:bg-violet-500/10" : "border-violet-100 bg-white hover:bg-violet-50"
                          }`}
                        >
                          <EntityAvatar name={a.ad_soyad} theme="rose" size="sm" />
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-medium ${textClass}`}>{a.ad_soyad}</p>
                            <p className={`text-[11px] ${labelClass}`}>{a.telefon || "Telefon yok"}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </EntityInfoBlock>
                )}
              </div>
              <DialogFooter className={`px-6 py-4 border-t ${isDark ? "border-slate-800 bg-slate-950/50" : "border-gray-100 bg-gray-50/50"}`}>
                <Button type="button" variant="outline" onClick={() => setDetailOpen(false)} className={isDark ? "border-slate-600" : ""}>Kapat</Button>
                {(selected.assistants || []).length > 0 && (
                  <Button type="button" variant="outline" onClick={() => { setDetailOpen(false); setAssistantsOpen(true); }} className={isDark ? "border-violet-500/40 text-violet-300" : "border-violet-200 text-violet-700"}>
                    <Users className="w-4 h-4 mr-1" /> Asistanlar
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => openEdit(selected)} className={isDark ? "border-sky-500/40 text-sky-300" : "border-sky-200 text-sky-700"}>
                  <Pencil className="w-4 h-4 mr-1" /> Düzenle
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Asistan listesi modal */}
      <Dialog open={assistantsOpen} onOpenChange={setAssistantsOpen}>
        <DialogContent className={`max-w-md ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-violet-500/15"><Users className="w-4 h-4 text-violet-500" /></span>
              Asistanlar — {selected?.ad_soyad}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto py-1">
            {(selected?.assistants || []).length === 0 ? (
              <p className={`text-sm text-center py-6 ${labelClass}`}>Bu doktora atanmış asistan yok.</p>
            ) : (selected?.assistants || []).map((a) => (
              <button
                key={a.id || a.ad_soyad}
                type="button"
                onClick={() => openAssistantDetail(a, selected)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all hover:scale-[1.01] ${
                  isDark ? "border-violet-500/25 bg-gradient-to-r from-violet-500/10 to-rose-500/5 hover:border-violet-400/40" : "border-violet-100 bg-gradient-to-r from-violet-50 to-rose-50/50 hover:border-violet-200"
                }`}
              >
                <EntityAvatar name={a.ad_soyad} theme="rose" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${textClass}`}>{a.ad_soyad}</p>
                  <p className={`text-xs ${labelClass}`}>{a.telefon || "—"} · {a.email || "E-posta yok"}</p>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAssistantsOpen(false)} className={isDark ? "border-slate-600" : ""}>Kapat</Button>
            <Button type="button" onClick={() => { setAssistantsOpen(false); openEdit(selected); }} className="bg-violet-600 hover:bg-violet-500 text-white">
              <Pencil className="w-4 h-4 mr-1" /> Asistanları Düzenle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asistan detay modal */}
      <Dialog open={assistantDetailOpen} onOpenChange={setAssistantDetailOpen}>
        <DialogContent className={`max-w-sm p-0 overflow-hidden border-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:hover:opacity-100 [&>button]:hover:bg-white/10 ${isDark ? "bg-slate-900 text-white" : "bg-white"}`}>
          {selectedAssistant && selected && (
            <>
              <EntityModalHero
                name={selectedAssistant.ad_soyad}
                subtitle={`Asistan · ${selected.ad_soyad}`}
                theme="emerald"
                icon={User}
              />
              <div className="px-6 pt-4 pb-2">
                <EntityInfoBlock title="İletişim Bilgileri" icon={Phone} tone="rose" isDark={isDark}>
                  <EntityDetailRow label="Telefon" value={selectedAssistant.telefon} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                  <EntityDetailRow label="E-posta" value={selectedAssistant.email} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>
                <EntityInfoBlock title="Bağlı Doktor" icon={Stethoscope} tone="sky" isDark={isDark} className="mt-3">
                  <EntityDetailRow label="Doktor" value={selected.ad_soyad} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Hastane" value={selected.hospital_ad} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>
              </div>
              <DialogFooter className={`px-6 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-100"}`}>
                <Button type="button" variant="outline" onClick={() => setAssistantDetailOpen(false)} className={isDark ? "border-slate-600" : ""}>Kapat</Button>
                <Button type="button" onClick={() => { setAssistantDetailOpen(false); openEdit(selected); }} className="bg-rose-600 hover:bg-rose-500 text-white">
                  <Pencil className="w-4 h-4 mr-1" /> Düzenle
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Form modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-sky-500/15"><Stethoscope className="w-4 h-4 text-sky-500" /></span>
              {editing ? "Doktor Düzenle" : "Yeni Doktor"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {isSuperAdmin && (
              <div className="sm:col-span-2">
                <label className={`text-xs font-medium ${labelClass}`}>Firma *</label>
                <Select value={form.company_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v, hospital_id: "" }))}>
                  <SelectTrigger className={`mt-1 h-9 ${inputClass}`}><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {companies.filter((c) => c.status === 1).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Hastane</label>
              <Select value={form.hospital_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, hospital_id: v === "none" ? "" : v }))}>
                <SelectTrigger className={`mt-1 h-9 ${inputClass}`}><SelectValue placeholder="Hastane seçin" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="none">Atanmamış</SelectItem>
                  {modalHospitals.map((h) => <SelectItem key={h.id} value={String(h.id)}>{h.ad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Doktor Ad Soyad *</label>
              <Input value={form.ad_soyad} onChange={(e) => setForm((f) => ({ ...f, ad_soyad: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div>
              <label className={`text-xs font-medium ${labelClass}`}>Telefon *</label>
              <Input value={form.telefon} onChange={(e) => setForm((f) => ({ ...f, telefon: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div>
              <label className={`text-xs font-medium ${labelClass}`}>E-posta</label>
              <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Uzmanlık</label>
              <Input value={form.uzmanlik} onChange={(e) => setForm((f) => ({ ...f, uzmanlik: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Notlar</label>
              <Textarea value={form.notlar} onChange={(e) => setForm((f) => ({ ...f, notlar: e.target.value }))} rows={2} className={`mt-1 ${inputClass}`} />
            </div>

            <div className={`sm:col-span-2 rounded-xl border p-3 ${isDark ? "border-violet-500/20 bg-violet-500/5" : "border-violet-100 bg-violet-50/40"}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className={`text-xs font-semibold ${isDark ? "text-violet-300" : "text-violet-800"}`}>Doktor Asistanları</p>
                <Button type="button" variant="outline" size="sm" onClick={addAssistant} className={`h-8 ${isDark ? "border-violet-500/30 text-violet-300" : "border-violet-200 text-violet-700"}`}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Ekle
                </Button>
              </div>
              {(form.assistants || []).length === 0 ? (
                <p className={`text-xs ${labelClass}`}>Henüz asistan eklenmedi.</p>
              ) : (
                <div className="space-y-2">
                  {(form.assistants || []).map((assistant, index) => (
                    <div key={`assistant-${index}`} className={`grid grid-cols-1 sm:grid-cols-12 gap-2 p-2.5 rounded-lg border ${isDark ? "border-rose-500/20 bg-rose-500/5" : "border-rose-100 bg-white"}`}>
                      <div className="sm:col-span-1 flex items-center justify-center">
                        <EntityAvatar name={assistant.ad_soyad || "?"} theme="rose" size="sm" />
                      </div>
                      <div className="sm:col-span-4">
                        <Input value={assistant.ad_soyad} onChange={(e) => updateAssistant(index, "ad_soyad", e.target.value)} placeholder="Ad soyad" className={`h-8 text-xs ${inputClass}`} />
                      </div>
                      <div className="sm:col-span-3">
                        <Input value={assistant.telefon} onChange={(e) => updateAssistant(index, "telefon", e.target.value)} placeholder="Telefon" className={`h-8 text-xs ${inputClass}`} />
                      </div>
                      <div className="sm:col-span-3">
                        <Input value={assistant.email} onChange={(e) => updateAssistant(index, "email", e.target.value)} placeholder="E-posta" className={`h-8 text-xs ${inputClass}`} />
                      </div>
                      <div className="sm:col-span-1 flex justify-end">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeAssistant(index)} className={`h-8 w-8 ${isDark ? "text-rose-400" : "text-rose-600"}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)} className={isDark ? "border-slate-600" : ""}>İptal</Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DoctorsPage;
